import React, {useState, useEffect} from 'react';
import {Datepicker} from '@zendeskgarden/react-datepickers';
import {dateFormatter} from '../../helper/util';
import {Label, MediaInput} from '@zendeskgarden/react-forms';
import {Controller, useForm} from 'react-hook-form';
import styled from 'styled-components';
import {Button} from '@zendeskgarden/react-buttons';
import {Col, Row} from '@zendeskgarden/react-grid';
import {useLaptopReplacementAsync} from '../../hooks/useLaptopReplacementAsync';
import {getCategory} from '../../resources/zendesk';
import ConfirmSerialNumberModal from './ConfirmSerialNumberModal';
import RepairAlert from './RepairAlert';

export type ReplacementValue = {
	actualReplacementDate: Date
};

const StyledField = styled.div`
  width: 100%;

  [data-garden-id="forms.faux_input"] {
      align-items: center;
      height: 40px;
	  width: 50%;
  }
`;

type Props = {
	purchaseDate: Date,
	sysId: string,
	planedReplacementDate: Date,
	customFieldId: string
}

const ReplacementForm = ({purchaseDate, sysId, planedReplacementDate, customFieldId}: Props) => {

    const { postReplacementDate, isUpdateReplacementLoading} = useLaptopReplacementAsync();
    const [showReplacementAlert, setShowReplacementAlert] = useState(false);
    const [replacementAlertMessage, setReplacementAlertMessage] = useState('Please select category as Hardware replacement');
    const [showSuccess, setShowSuccess] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [pendingReplacementValue, setPendingReplacementValue] = useState<ReplacementValue | null>(null);
    const [isModalLoading, setIsModalLoading] = useState(false);

    const {
        handleSubmit,
        control,
        reset,
        getValues,
        watch,
        formState: { errors, isValid},
    } = useForm<ReplacementValue>({
	    defaultValues: {
		    actualReplacementDate: planedReplacementDate
	    }
    });

    const onSubmit = async (values: ReplacementValue) => {
        setPendingReplacementValue(values);
        setShowConfirmModal(true);
    };

    const handleModalNo = () => {
        setShowConfirmModal(false);
        setPendingReplacementValue(null);
    };

    const handleModalYes = async () => {
        if (!pendingReplacementValue) return;
        setIsModalLoading(true);
        // get zendesk category
        const categoryStr = await getCategory(customFieldId);
        if (categoryStr && categoryStr.split('__')[0] == 'hardware_replacement') {
            const requestBody = {
                sysId: sysId,
                date: pendingReplacementValue.actualReplacementDate
            };
            try {
                await postReplacementDate(requestBody);
                setShowSuccess(true);
            } catch (e) {
                setReplacementAlertMessage('Oops! Something went wrong. Please try again later.');
                setShowReplacementAlert(true);
            }
        } else {
            setShowReplacementAlert(true);
        }
        setIsModalLoading(false);
        setShowConfirmModal(false);
        setPendingReplacementValue(null);
    };

    useEffect(() => {
        if (showSuccess) {
            const timer = setTimeout(() => setShowSuccess(false), 10000);
            return () => clearTimeout(timer);
        }
    }, [showSuccess]);

    useEffect(() => {
        if (showReplacementAlert) {
            const timer = setTimeout(() => setShowReplacementAlert(false), 10000);
            return () => clearTimeout(timer);
        }
    }, [showReplacementAlert]);

    return(
        <>
            <ConfirmSerialNumberModal
                open={showConfirmModal}
                onYes={handleModalYes}
                onNo={handleModalNo}
                isLoading={isModalLoading}
            />
            <form onSubmit={handleSubmit(onSubmit)}>
	            <Row>
		            <Col sm={8} style={{marginBottom: '12px'}}>
                        <StyledField className={'date-picker-field'}>
                            <Label>Actual Replacement Date</Label>
                            <Controller control={control}
                                name={'actualReplacementDate'}
                                render={
                                    ({ field: { onChange, value } }) =>(
                                        <Row alignItems="center" style={{marginLeft: '1px', marginTop: '8px',gap: '6px'}}>
                                            <Datepicker key="actual-replacement-date"
                                                style={{ alignItems: 'center', backgroundColor: 'red !important' }}
                                                formatDate={date => dateFormatter.format(date)}
                                                onChange={onChange}
                                                value={value}
                                                minValue={purchaseDate}>
                                                <MediaInput className={'date-picker-part'}/>
                                            </Datepicker>
                                            <Button size="small" type={'submit'} disabled={isUpdateReplacementLoading}>Submit</Button>
                                        </Row>
                                    )
                                } />
                        </StyledField>
		            </Col>
                    {showReplacementAlert && (
                        <Col sm={8}>
                            <RepairAlert
                                message={replacementAlertMessage}
                                onClose={() => setShowReplacementAlert(false)}
                                type="error"
                            />
                        </Col>
                    )}
                    {showSuccess && (
                        <Col sm={8}>
                            <RepairAlert
                                message="Replacement date updated successfully!"
                                onClose={() => setShowSuccess(false)}
                                type="success"
                            />
                        </Col>
                    )}
	            </Row>
            </form>
        </>
    );};

export default ReplacementForm;